import _uniqueId from 'lodash/uniqueId'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'

import {displayToasterMessage} from 'store/actions'
import isMobileVersion from 'store/mobile'
import {validateEmail} from 'store/validations'

import Button from 'components/button'
import {colorToAlpha} from 'components/colors'
import Input from 'components/input'
import Trans from 'components/i18n_trans'
import {useModal} from 'components/modal'
import {SmoothTransitions} from 'components/theme'

import type {DispatchAllUpskillingActions} from '../store/actions'
import {sendUpskillingFeedbackVolunteering} from '../store/actions'

import Modal from './modal'

const sectionTitleStyle: React.CSSProperties = {
  alignItems: 'center',
  color: colors.TEXT,
  display: 'flex',
  fontFamily: config.titleFont ? config.titleFont : 'Lato, Helvetica',
  fontSize: isMobileVersion ? 16 : 22,
  fontWeight: 'bold',
  margin: 0,
}
const feedbackModalStyle: React.CSSProperties = {
  background: colors.FEEDBACK_STARS_BACKGROUND,
  color: '#fff',
  padding: 50,
}
const feedbackModalTitleStyle: React.CSSProperties = {
  fontSize: 30,
  margin: '0px 5px 0 0',
}
const feedbackModalInputStyle: React.CSSProperties = {
  backgroundColor: colorToAlpha(colors.WARM_GREY, .1),
  borderRadius: 4,
  color: 'inherit',
  fontFamily: 'inherit',
}
const inputErrorStyle: React.CSSProperties = {
  ...feedbackModalInputStyle,
  borderColor: colors.ERROR_RED,
}
const feedbackBannerDesktopStyle: React.CSSProperties = {
  color: 'white',
  display: 'block',
  margin: '20px auto 2px',
  maxWidth: 500,
  textAlign: 'center',
}
const feedbackButtonStyle: RadiumCSSProperties = {
  ':focus': {
    background: colors.JOB_BACKGROUND_HOVER,
    color: colors.WHITE,
  },
  ':hover': {
    background: colors.JOB_BACKGROUND_HOVER,
    color: colors.WHITE,
  },
  'background': colors.WHITE,
  'color': colors.CHARCOAL_GREY_TWO,
  'fontSize': 16,
  'fontWeight': 'bold',
  ...SmoothTransitions,
}
const emailButtonStyle: RadiumCSSProperties = {
  ':focus': {
    outline: 'solid 2px',
  },
  ':hover': {
    outline: 'solid 2px',
  },
  'marginTop': 20,
  ...SmoothTransitions,
}
const FeedbackRequest = ({onDone}: {onDone: (isFeedbackBannerDisplayed: boolean) => void}):
React.ReactElement => {
  const {t} = useTranslation()
  const dispatch: DispatchAllUpskillingActions = useDispatch()
  const [isShown, showModal, closeModal] = useModal()
  const [email, setEmail] = useState('')
  const [hasValidated, setHasValidated] = useState(false)
  const [hasSentFeedbackVolunteering, setSentFeedbackVolunteering] = useState(false)
  const handleEmailChange = useCallback((email: string) => {
    setEmail(email.toLocaleLowerCase())
    setHasValidated(true)
  }, [setEmail])
  const isValidEmail = useMemo(() => {
    return !hasValidated || (email !== '' && validateEmail(email))
  }, [email, hasValidated])
  const errorMessageStyle = useMemo((): React.CSSProperties => ({
    color: colors.ERROR_RED,
    display: isValidEmail ? 'none' : 'block',
    fontSize: 15,
    marginTop: 10,
  }), [isValidEmail])
  const feedbackCta = hasSentFeedbackVolunteering ?
    <p role="status">{t("Merci d'être volontaire, nous vous recontacterons sous peu.")}</p> :
    <Button type="outline" onClick={showModal} style={feedbackButtonStyle}>
      {t('Je donne mon avis')}</Button>
  const feedbackTitle = t('Gagnez 50€ pour donner votre avis (places limitées)')
  const feedbackText = <Trans parent="p" style={{margin: '25px 0'}}>
    Nous cherchons encore <strong>11 personnes</strong> pour donner leur avis
    sur {{productName: config.productName}}. Inscrivez-vous vite&nbsp;!
  </Trans>
  const emailLabelId = useMemo(_uniqueId, [])
  const titleId = useMemo(_uniqueId, [])
  const errorMessageId = useMemo(_uniqueId, [])
  const inputRef = useRef<HTMLInputElement>(null)
  const confirmMessage = t("Merci d'être volontaire, nous vous recontacterons sous peu.")
  const onSubmit = useCallback(() => {
    setHasValidated(true)
    if (!isValidEmail || email === '') {
      inputRef.current?.focus()
      return
    }
    dispatch(sendUpskillingFeedbackVolunteering(email))
    setSentFeedbackVolunteering(true)
    closeModal()
    dispatch(displayToasterMessage(confirmMessage))
    onDone(false)
  }, [closeModal, confirmMessage, dispatch, email, isValidEmail, onDone])

  const modal = <Modal
    {...{isShown}} style={feedbackModalStyle} aria-labelledby={titleId} onClose={closeModal}>
    <div style={{maxWidth: 560}}>
      <h3 style={feedbackModalTitleStyle}>{t('Merci beaucoup\u00A0!')}</h3>
      <p>{t('À quelle adresse email pouvons-nous vous joindre\u00A0?')}</p>
      <form onSubmit={onSubmit}>
        <Input
          type="email" placeholder={t('Saisissez votre adresse email')}
          aria-labelledby={emailLabelId} name="email" value={email} onEdit={handleEmailChange}
          style={!isValidEmail ? inputErrorStyle : feedbackModalInputStyle} aria-required={true}
          onChangeDelayMillisecs={2000} ref={inputRef} aria-describedby={errorMessageId} />
        <p id={errorMessageId} style={errorMessageStyle}>{email === '' ?
          t('Veuillez renseigner votre adresse email.') :
          t('Votre adresse email est incorrecte, veuillez la corriger.')}</p>
      </form>
      <Button type="navigation" onClick={onSubmit} style={emailButtonStyle}>
        {t('Valider mon adresse email')}
      </Button>
    </div>
  </Modal>

  return <div style={isMobileVersion ? {display: 'block'} : feedbackBannerDesktopStyle}>
    <h2 style={sectionTitleStyle} id={titleId}>{feedbackTitle}</h2>
    {feedbackText}{feedbackCta}{modal}</div>
}

export default React.memo(FeedbackRequest)
